import { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { EmptyState } from '@/components/EmptyState';
import { ProvisioningStatusPanel } from '@/components/ProvisioningStatusPanel';

interface Onboarding {
  id: string;
  employee_name: string;
  status: string;
  start_date: string;
  target_completion_date: string;
  progress: number;
  tasks: Array<{ id: string; title: string; completed: boolean }>;
  assigned_buddy_name?: string;
}

export function OnboardingPage() {
  const { data, loading } = useApi<{ onboarding: Onboarding[] }>('onboarding');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (loading) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <UserPlus className="w-5 h-5 text-amber-500" /> Active Onboarding
      </h2>
      {(data?.onboarding ?? []).length === 0 ? (
        <EmptyState
          icon={<UserPlus className="w-12 h-12" />}
          title="No active onboarding"
          description="New hire onboarding processes will appear here."
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {data?.onboarding.map(o => (
            <Card key={o.id}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{o.employee_name}</p>
                  <p className="text-xs text-muted-foreground">Started {o.start_date} &middot; Target {o.target_completion_date}</p>
                </div>
                <StatusBadge status={o.status} />
              </div>
              {/* Progress bar */}
              <div className="w-full bg-surface-3 rounded-full h-2 mb-2">
                <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${o.progress}%` }} />
              </div>
              <p className="text-xs text-muted-foreground">{o.progress}% complete &middot; {o.tasks.filter(t => t.completed).length}/{o.tasks.length} tasks</p>
              {o.assigned_buddy_name && <p className="text-xs text-muted-foreground mt-1">Buddy: {o.assigned_buddy_name}</p>}
              <button
                onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}
                className="mt-3 text-xs text-amber-500 hover:text-amber-400"
              >
                {expandedId === o.id ? 'Hide' : 'Show'} provisioning status
              </button>
              {expandedId === o.id && (
                <div className="mt-3">
                  <ProvisioningStatusPanel onboardingId={o.id} />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
